import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eraser, Search, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";

// Finds leftover demo accounts (@compasskids.app or demo.*) and deletes the selected ones.
export default function DemoCleanupCard() {
  const [accounts, setAccounts] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const find = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/demo-accounts");
      setAccounts(data);
      setSelected(new Set(data.map((a) => a.user_id)));
    } catch (e) {
      toast.error(errMsg(e, "Could not load demo accounts"));
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const purge = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/demo-accounts/delete", { user_ids: [...selected], confirm: confirmText });
      toast.success(`Deleted ${data.deleted} demo account${data.deleted === 1 ? "" : "s"}`);
      setConfirmOpen(false);
      setConfirmText("");
      await find();
    } catch (e) {
      toast.error(errMsg(e, "Could not delete demo accounts"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm max-w-2xl" data-testid="demo-cleanup-card">
      <h3 className="font-heading font-bold text-forest mb-1 flex items-center gap-2"><Eraser className="w-5 h-5 text-terracotta-dark" /> Demo account cleanup</h3>
      <p className="text-sm text-forest/60 mb-4">Finds accounts with an email ending in <strong>@compasskids.app</strong> or starting with <strong>demo.</strong> Admin accounts are never included.</p>
      <Button onClick={find} disabled={loading} variant="outline" className="rounded-full border-sage/40 text-sage-dark hover:bg-sage-light" data-testid="demo-cleanup-find-btn">
        <Search className="w-4 h-4 mr-1.5" /> {accounts ? "Refresh list" : "Find demo accounts"}
      </Button>

      {accounts && accounts.length === 0 && (
        <p className="mt-4 text-sm text-sage-dark font-semibold" data-testid="demo-cleanup-empty">No demo accounts found. You're all clean.</p>
      )}

      {accounts && accounts.length > 0 && (
        <div className="mt-4 space-y-3">
          <ul className="divide-y divide-sage/10 border border-sage/20 rounded-2xl overflow-hidden max-h-80 overflow-y-auto" data-testid="demo-cleanup-list">
            {accounts.map((a) => (
              <li key={a.user_id} className="flex items-center gap-3 px-4 py-2.5 bg-white/60 hover:bg-softstone/40 transition-colors" data-testid={`demo-account-row-${a.user_id}`}>
                <Checkbox checked={selected.has(a.user_id)} onCheckedChange={() => toggle(a.user_id)} data-testid={`demo-account-checkbox-${a.user_id}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-forest truncate">{a.email}</p>
                  <p className="text-xs text-forest/50">{a.role} · joined {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</p>
                </div>
              </li>
            ))}
          </ul>
          <Button onClick={() => setConfirmOpen(true)} disabled={!selected.size || loading} className="rounded-full bg-terracotta-dark hover:bg-terracotta text-white" data-testid="demo-cleanup-delete-btn">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete selected ({selected.size})
          </Button>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setConfirmText(""); }}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="demo-cleanup-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-forest">Delete {selected.size} demo account{selected.size === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription>This permanently removes the accounts and all their profiles, subscriptions, listings and notifications. Type <strong>DELETE</strong> to confirm.</DialogDescription>
          </DialogHeader>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="rounded-xl" data-testid="demo-cleanup-confirm-input" />
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setConfirmOpen(false)} data-testid="demo-cleanup-cancel-btn">Cancel</Button>
            <Button onClick={purge} disabled={confirmText !== "DELETE" || loading} className="rounded-full bg-terracotta-dark hover:bg-terracotta text-white" data-testid="demo-cleanup-confirm-btn">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
